import { ReactiveController, ReactiveControllerHost } from 'lit';
import { ChangeMessage } from '@tanstack/db';
import { AuthService } from '../services/auth-service';

// Really needed as tanstack/db does not export the class used, so extracting the minimal interface
export interface LiveQueryCollectionApi {
  startSyncImmediate: () => void;
  subscribeChanges: (
    callback: (changes: ChangeMessage[]) => void,
    options: { includeInitialState?: boolean }
  ) => { unsubscribe: () => void };
  isReady: () => boolean;
}

/**
 * Lit Controller to integrate a TanStack DB Live Query Collection.
 */
export class LiveQueryController<TCollection extends LiveQueryCollectionApi> implements ReactiveController {
  private host: ReactiveControllerHost;
  private authState: AuthService;

  public collection?: TCollection;
  private subscription?: { unsubscribe: () => void };
  private changeHandler: ((collection: TCollection) => void) | undefined;
  private initCollection: () => { collection: TCollection };

  constructor(
    host: ReactiveControllerHost,
    authState: AuthService,
    init: () => { collection: TCollection },
    handleChange?: (collection: TCollection) => void
  ) {
    this.host = host;
    this.authState = authState;
    this.changeHandler = handleChange;
    this.initCollection = init;

    host.addController(this);
  }

  authStateChanged = () => {
    this.updateSubscription();
  }

  hostConnected() {
    this.authState.addEventListener('authStateChanged', this.authStateChanged);
    this.updateSubscription();
  }

  hostDisconnected() {
    this.cleanupSubscription();
    this.authState.removeEventListener('authStateChanged', this.authStateChanged);
  }

  private updateSubscription() {
    if (this.authState.state.isAuth) {
      try {
        // Only re-initialize if not already connected or if logic requires it
        // For now, we follow the original pattern of re-initializing on auth change
        if (!this.collection) {
          this.collection = this.initCollection().collection;
        }

        if (!this.subscription && this.collection) {
          this.subscription = this.collection.subscribeChanges(
            () => {
              queueMicrotask(() => {
                try {
                  if (this.changeHandler) {
                    this.changeHandler(this.collection!);
                  }
                  this.host.requestUpdate();
                } catch (err) {
                  console.error('Error in LiveQueryController change handler:', err);
                }
              });
            },
            { includeInitialState: true }
          );
          this.collection.startSyncImmediate();
        }
      } catch (err) {
        console.error('Failed to initialize LiveQueryController subscription:', err);
        // Ensure we don't end up in a half-baked state
        this.cleanupSubscription();
      }
    } else {
      this.cleanupSubscription();
    }
    this.host.requestUpdate();
  }

  private cleanupSubscription() {
    if (this.subscription) {
      try {
        this.subscription.unsubscribe();
      } catch (err) {
        console.warn('Error unsubscribing from LiveQueryController:', err);
      }
      this.subscription = undefined;
    }
    // We might want to keep the collection reference or clear it.
    // Original code cleared it.
    this.collection = undefined;
  }

  get isReady(): boolean {
    try {
      return this.authState.state.isAuth && !!this.collection && this.collection.isReady();
    } catch {
      return false;
    }
  }
}
